import * as React from 'react';
import {LegacyModal} from '@flexe/ui-components';
import axios from 'axios';

const DEFAULT_SESSION_TIMEOUT = 1000 * 1800; // 30 min - production default
const IDLE_MODAL_LIFESPAN = 1000 * 60 * 5; // 5 min
const ACTIVITY_CHECK_INTERVAL = 1000 * 60; // 1 min
const BUFFER = 1000 * 10; // 10 sec

const EVENTS = [
  'mousemove',
  'keydown',
  'wheel',
  'DOMMouseScroll',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'MSPointerDown',
  'MSPointerMove',
  'visibilitychange'
];

const FLEXE_MESSAGE_KEY = 'flexe_message';

interface IdleMonitorProp {
  sessionTimeoutInSeconds: number;
  logout: (event: Event) => void;
}

interface IdleMonitorState {
  modalOpen: boolean;
  countdown: number;
  isUserActive: boolean;
  sessionTimeoutDuration: number;
}

class IdleMonitor extends React.Component<IdleMonitorProp, IdleMonitorState> {
  private idleModalTimer;
  private idleModalEndTimer;
  private checkUserActionTimer;
  private countdownInterval;

  constructor(props) {
    super(props);

    this.state = {
      modalOpen: false,
      countdown: 0,
      isUserActive: false,
      sessionTimeoutDuration: DEFAULT_SESSION_TIMEOUT
    };
  }

  public componentDidMount() {
    this.idleModalTimer = null;
    this.idleModalEndTimer = null;
    this.checkUserActionTimer = null;
    this.countdownInterval = null;

    EVENTS.forEach((event) => {
      window.addEventListener(event, this.updateIsUserActive);
    });

    window.addEventListener('storage', this.onLocalStorageUpdate);
    this.idleModalTimer = setTimeout(this.onIdle, this.state.sessionTimeoutDuration - IDLE_MODAL_LIFESPAN - BUFFER);

    this.extendSession(true); // Sets timeout to true server setting and validUntilTime
  }

  public render() {
    return (
      <div>
        <LegacyModal
          id="session-timeout-warning"
          title="Your session is about to expire"
          show={this.state.modalOpen}
          size="small"
          toggleModal={null}
          footer={
            <div>
              <a onClick={(event) => this.handleLogout(event)}>No, sign me out</a>
              <button
                id="perform-stay-in"
                type="button"
                className="btn btn-clear"
                onClick={(event) => this.handleStayLoggedIn()}
              >
                Yes, keep me signed in
              </button>
            </div>
          }
          hideClose={true}
        >
          <div style={{color: 'black'}}>
            <p>You will be logged out in {this.formatCountdownTime(this.state.countdown)}.</p>
            <p>Do you want to stay signed in?</p>
          </div>
        </LegacyModal>
      </div>
    );
  }

  private updateIsUserActive = () => {
    this.setState({isUserActive: true});
  };

  private onIdle = () => {
    clearTimeout(this.idleModalTimer);
    clearTimeout(this.idleModalEndTimer);
    clearTimeout(this.checkUserActionTimer);

    if (this.getNextTimeoutLocalStorage() - IDLE_MODAL_LIFESPAN > Date.now()) {
      this.idleModalTimer = setTimeout(
        this.onIdle,
        this.getNextTimeoutLocalStorage() - IDLE_MODAL_LIFESPAN - Date.now()
      );
      return;
    }

    this.setState({modalOpen: true});
    this.setState({countdown: IDLE_MODAL_LIFESPAN});
    this.countdownInterval = setInterval(this.updateCountDown, 1000);
    this.idleModalEndTimer = setTimeout((event) => this.handleLogout(event), IDLE_MODAL_LIFESPAN);
  };

  private extendSession = (forced = false) => {
    if (forced || this.state.isUserActive) {
      axios.get('/extend_session').then((resp) => {
        const newSessionTimeoutInSeconds = parseInt(resp.data, 10);
        if (isNaN(newSessionTimeoutInSeconds)) {
          // Somehow the session expired already so logout
          this.handleLogout(null);
        } else {
          // Session has been extended successfully
          if (newSessionTimeoutInSeconds === 0) {
            // No session timeout so stop IdleMonitor
            this.stopIdleMonitor();
          } else {
            // Reset modalTimer
            this.setState({sessionTimeoutDuration: newSessionTimeoutInSeconds * 1000});
            clearTimeout(this.idleModalTimer);
            this.idleModalTimer = setTimeout(
              this.onIdle,
              this.state.sessionTimeoutDuration - IDLE_MODAL_LIFESPAN - BUFFER
            );

            this.setLocalStorage({validUntilTime: Date.now() + this.state.sessionTimeoutDuration});
          }
        }
      });
    }
    this.checkUserActionTimer = setTimeout(this.extendSession, ACTIVITY_CHECK_INTERVAL);
    this.setState({isUserActive: false});
  };

  private stopIdleMonitor = () => {
    this.setState({modalOpen: false});
    clearTimeout(this.idleModalTimer);
    clearTimeout(this.idleModalEndTimer);
    clearTimeout(this.checkUserActionTimer);
    clearInterval(this.countdownInterval);
  };

  private handleLogout = (event) => {
    if (this.getNextTimeoutLocalStorage() > Date.now()) {
      clearTimeout(this.idleModalTimer);
      this.idleModalTimer = setTimeout(
        this.onIdle,
        this.getNextTimeoutLocalStorage() - IDLE_MODAL_LIFESPAN - Date.now()
      );
      return;
    }

    this.setLocalStorage({validUntilTime: -1});
    this.logout(event);
  };

  private logout = (event) => {
    this.setState({modalOpen: false});
    clearTimeout(this.idleModalTimer);
    clearTimeout(this.idleModalEndTimer);
    clearTimeout(this.checkUserActionTimer);
    clearInterval(this.countdownInterval);
    this.props.logout(event);
  };

  private handleStayLoggedIn = () => {
    this.extendSession(true);
    this.setState({modalOpen: false});
    this.setState({countdown: 0});
    clearTimeout(this.idleModalEndTimer);
    clearInterval(this.countdownInterval);
  };

  private updateCountDown = () => {
    if (this.state.modalOpen && this.state.countdown > 0) {
      this.setState({countdown: this.state.countdown - 1000});
    }
  };

  private formatCountdownTime = (msec) => {
    const min = Math.floor(msec / 60000);
    const sec = (msec % 60000) / 1000;

    if (min > 0) {
      return min + ' min ' + sec + ' sec';
    } else {
      return sec + ' sec';
    }
  };

  private setLocalStorage = (message) => {
    const msg = JSON.stringify(message);
    window.localStorage.setItem(FLEXE_MESSAGE_KEY, msg);
  };

  private getNextTimeoutLocalStorage = () => {
    return JSON.parse(window.localStorage.getItem(FLEXE_MESSAGE_KEY)).validUntilTime;
  };

  private onLocalStorageUpdate = (event) => {
    if (event.key !== FLEXE_MESSAGE_KEY) {
      return;
    }
    const message = JSON.parse(event.newValue);
    if (!message) {
      return;
    }

    this.handleTimeoutUpdate(message.validUntilTime);
  };

  private handleTimeoutUpdate = (validUntilTime) => {
    if (validUntilTime > 0) {
      this.setState({modalOpen: false});
      this.setState({countdown: 0});
      clearTimeout(this.idleModalTimer);
      clearTimeout(this.idleModalEndTimer);
      clearTimeout(this.checkUserActionTimer);
      clearInterval(this.countdownInterval);

      this.idleModalTimer = setTimeout(this.onIdle, validUntilTime - IDLE_MODAL_LIFESPAN - BUFFER);
      this.checkUserActionTimer = setTimeout(this.extendSession, ACTIVITY_CHECK_INTERVAL);
      this.setState({isUserActive: false});
    } else {
      this.logout(null);
    }
  };
}

export default IdleMonitor;
